import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import type {
	SmartField_Api_Contracts_CoordinateDto,
	SmartField_Api_Contracts_ExperimentalLocation_ExperimentalLocationDto,
	SmartField_Api_Contracts_Mapping_ChamberMappingDto,
	SmartField_Api_Contracts_Mapping_TowerMappingDto,
	SmartField_Api_Contracts_Sensor_DimSensorDto,
	SmartField_Api_Contracts_Sensor_DimSensorPlacementDto,
	SmartField_Api_Contracts_Sensor_DimSensorPropertiesUpdateDto,
	SmartField_Api_Contracts_Site_SiteDto,
	SmartField_DAL_Models_ExperimentalLocation_ExperimentalLocationState,
} from "../../api";
import { sensorGetPlacementHistory } from "../../api";
import { HeightHistoryChart } from "./HeightHistoryChart";
import { PlacementHistoryTimeline } from "./PlacementHistoryTimeline";
import type { PlacementHistoryEntry } from "./usePlacementHistory";
import { parseTowerId, towerChannels } from "./sensorConstants";

const WIND_SENSOR_TYPES = new Set([
	"WindSpeed",
	"WindDirection",
	"WindVector3D",
	"WindGust",
	"SonicTemperature",
]);

const SENSOR_TYPE_LABELS: Record<string, string> = {
	AirTemperature: "Air Temp",
	RelativeAirHumidity: "RH",
	AirPressure: "Air Pressure",
	SoilTemperature: "Soil Temp",
	SoilMoisture: "Soil Moisture",
	VolumetricWaterContent: "VWC",
	MatricPotential: "Matric Pot.",
	SoilAbsolutePressure: "Soil Abs. Pressure",
	SaturationExtractEC: "Sat. Extract EC",
	NeutronCount: "Neutron Count",
	NitricOxide: "NO",
	NitrogenDioxide: "NO₂",
	CarbonDioxide: "CO₂",
	NitrousOxide: "N₂O",
	Ammonia: "NH₃",
	WaterVapor: "H₂O",
	Ozone: "O₃",
	Methane: "CH₄",
	Ethane: "C₂H₆",
	N2OIsotopologue446: "N₂O 446",
	N2OIsotopologue448: "N₂O 448",
	N2OIsotopomer456: "N₂O 456",
	N2OIsotopomer546: "N₂O 546",
	Precipitation: "Precip.",
	WindSpeed: "Wind Speed",
	WindDirection: "Wind Dir.",
	WindVector3D: "Wind 3D",
	WindGust: "Wind Gust",
	SolarRadiation: "Solar Rad.",
	NetRadiation: "Net Rad.",
	IncidentShortWave: "SW In",
	ReflectedShortWave: "SW Out",
	EmittedLongWave: "LW Out",
	IncidentLongWave: "LW In",
	SonicTemperature: "Sonic Temp.",
	VaporPressure: "Vapor Pressure",
	VaporPressureDeficit: "VPD",
	CropTargetHeight: "Crop Target H.",
	CropMaxHeight: "Crop Max H.",
	CropMinHeight: "Crop Min H.",
	CropMeanHeight: "Crop Mean H.",
	CropHeightStdDev: "Crop H. StdDev",
	XAxisLevel: "X Level",
	YAxisLevel: "Y Level",
};

function humanizeSensorType(type?: string | null): string {
	if (!type) return "";
	return SENSOR_TYPE_LABELS[type] ?? type;
}

function computeCentroid(
	coords: SmartField_Api_Contracts_CoordinateDto[],
): SmartField_Api_Contracts_CoordinateDto | null {
	if (!coords || coords.length === 0) {
		return null;
	}
	const sumX = coords.reduce((acc, c) => acc + (c.x ?? 0), 0);
	const sumY = coords.reduce((acc, c) => acc + (c.y ?? 0), 0);
	return { x: sumX / coords.length, y: sumY / coords.length };
}

type Props = {
	sensors: SmartField_Api_Contracts_Sensor_DimSensorDto[];
	coords?: SmartField_Api_Contracts_CoordinateDto;
	plots: SmartField_Api_Contracts_ExperimentalLocation_ExperimentalLocationDto[];
	fields: SmartField_Api_Contracts_ExperimentalLocation_ExperimentalLocationDto[];
	blocks?: SmartField_Api_Contracts_ExperimentalLocation_ExperimentalLocationDto[];
	miniBlocks?: SmartField_Api_Contracts_ExperimentalLocation_ExperimentalLocationDto[];
	allTimeEls?: SmartField_Api_Contracts_ExperimentalLocation_ExperimentalLocationDto[];
	chambers?: SmartField_Api_Contracts_Mapping_ChamberMappingDto[];
	towers?: SmartField_Api_Contracts_Mapping_TowerMappingDto[];
	site: SmartField_Api_Contracts_Site_SiteDto | null;
	lastEffectiveDates: { from: string; to: string } | null;
	onCoordsChange: (coords: SmartField_Api_Contracts_CoordinateDto) => void;
	onSavePlacement: (
		data: SmartField_Api_Contracts_Sensor_DimSensorPlacementDto,
	) => void;
	onSaveHeights: (
		data: SmartField_Api_Contracts_Sensor_DimSensorPropertiesUpdateDto,
	) => void;
	onCancel: () => void;
	isSavingPlacement: boolean;
	isSavingHeights: boolean;
	canEdit: boolean;
};

function toUtcDateString(d: Date): string {
	const pad = (n: number) => String(n).padStart(2, "0");
	return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

// API dates may lack a timezone suffix (unspecified DateTime kind serializes
// without "Z"), which `new Date(...)` then parses as local time. Force UTC
// interpretation by appending "Z" when no offset is present.
export function parseApiDateAsUtc(s: string): Date {
	return new Date(/[zZ]|[+-]\d\d:\d\d$/.test(s) ? s : `${s}Z`);
}

function dateStringToUtcIso(s: string): string {
	return `${s}T00:00:00.000Z`;
}

export function SensorPlacementPanel({
	sensors,
	coords,
	plots,
	fields,
	blocks = [],
	miniBlocks = [],
	allTimeEls = [],
	chambers = [],
	towers = [],
	site,
	lastEffectiveDates,
	onCoordsChange,
	onSavePlacement,
	onSaveHeights,
	onCancel,
	isSavingPlacement,
	isSavingHeights,
	canEdit,
}: Props) {
	const primarySensor = sensors[0];
	const now = new Date();

	const [towerHeight, setTowerHeight] = useState<string>(
		primarySensor?.towerHeightInMeters != null
			? String(primarySensor.towerHeightInMeters)
			: "",
	);

	const [lat, setLat] = useState<string>(
		coords?.y != null ? String(coords.y) : "",
	);
	const [lng, setLng] = useState<string>(
		coords?.x != null ? String(coords.x) : "",
	);
	const [scope, setScope] =
		useState<SmartField_DAL_Models_ExperimentalLocation_ExperimentalLocationState>(
			primarySensor?.currentScope ?? "Site",
		);
	const [elId, setElId] = useState<string>(
		primarySensor?.currentExperimentalLocationId != null
			? String(primarySensor.currentExperimentalLocationId)
			: "",
	);
	const [effectiveFrom, setEffectiveFrom] = useState(
		lastEffectiveDates?.from ??
			(primarySensor?.currentValidFrom
				? toUtcDateString(parseApiDateAsUtc(primarySensor.currentValidFrom))
				: toUtcDateString(now)),
	);
	const [effectiveTo, setEffectiveTo] = useState(lastEffectiveDates?.to ?? "");
	const [selectedChamberId, setSelectedChamberId] = useState("");
	const [selectedTowerId, setSelectedTowerId] = useState("");

	useEffect(() => {
		if (coords?.y != null) setLat(String(coords.y));
		if (coords?.x != null) setLng(String(coords.x));
	}, [coords]);

	const placementHistoryQuery = useQuery({
		queryKey: ["sensor-placement-history", primarySensor?.id],
		queryFn: () => sensorGetPlacementHistory({ id: primarySensor!.id! }),
		enabled: primarySensor?.id != null,
	});
	const placementHistoryEntries: PlacementHistoryEntry[] = primarySensor?.id != null
		? (placementHistoryQuery.data ?? []).map((entry) => ({
				...entry,
				sensorId: primarySensor.id!,
				sensorLabel: primarySensor.friendlyName ?? `Channel ${primarySensor.channelNumber}`,
			}))
		: [];

	if (!primarySensor) return null;

	const sortedPlots = [...plots].sort(
		(a, b) =>
			(a.fieldId ?? 0) - (b.fieldId ?? 0) || (a.plotId ?? 0) - (b.plotId ?? 0),
	);
	const sortedFields = [...fields].sort(
		(a, b) => (a.fieldId ?? 0) - (b.fieldId ?? 0),
	);
	const sortedBlocks = [...blocks].sort(
		(a, b) => (a.fieldId ?? 0) - (b.fieldId ?? 0),
	);
	const sortedMiniBlocks = [...miniBlocks].sort(
		(a, b) => (a.fieldId ?? 0) - (b.fieldId ?? 0),
	);

	const listForScope = (
		s: SmartField_DAL_Models_ExperimentalLocation_ExperimentalLocationState,
	) => {
		switch (s) {
			case "Plot":
				return plots;
			case "Field":
				return fields;
			case "Block":
				return blocks;
			case "MiniBlock":
				return miniBlocks;
			default:
				return [];
		}
	};

	const hasAerisSensor = sensors.some((s) => s.manufacturer === "Aeris");
	const hasMiroSensor = sensors.some((s) => s.manufacturer === "Miro");
	const hasWindSensor = sensors.some(
		(s) => s.measureName != null && WIND_SENSOR_TYPES.has(s.measureName),
	);
	const hasSonicSensor = sensors.some((s) => s.manufacturer === "Metek");
	const miroChannel = hasMiroSensor ? primarySensor.channelNumber : null;
	const towerOptions =
		miroChannel == null
			? towers
			: towers.filter((t) => {
					const towerId = parseTowerId(t.towerName);
					if (towerId == null) {
						return false;
					}
					const { lower, upper } = towerChannels(towerId);
					return miroChannel === lower || miroChannel === upper;
				});
	const channelLabel =
		primarySensor.friendlyName ??
		(primarySensor.channelNumber != null && primarySensor.channelNumber > 0
			? `Channel ${primarySensor.channelNumber}`
			: "Channel");
	const handleScopeChange = (
		newScope: SmartField_DAL_Models_ExperimentalLocation_ExperimentalLocationState,
	) => {
		setScope(newScope);
		setElId("");
		setSelectedChamberId("");
		setSelectedTowerId("");
		if (newScope === "Site" && site) {
			const centroid =
				site.shapeCoordinates && site.shapeCoordinates.length > 0
					? computeCentroid(site.shapeCoordinates)
					: (site.location ?? null);
			setLat(centroid ? String(centroid.y ?? "") : "");
			setLng(centroid ? String(centroid.x ?? "") : "");
			if (centroid) {
				onCoordsChange(centroid);
			}
		}
	};

	const handleElChange = (
		id: string,
		scopeOverride?: SmartField_DAL_Models_ExperimentalLocation_ExperimentalLocationState,
		fromQuickPick = false,
	) => {
		setElId(id);
		if (!fromQuickPick) {
			setSelectedChamberId("");
			setSelectedTowerId("");
		}
		const list = listForScope(scopeOverride ?? scope);
		const el = list.find((e) => String(e.id) === id);
		if (!el) {
			return;
		}
		const centroid =
			el.shapeCoordinates && el.shapeCoordinates.length > 0
				? computeCentroid(el.shapeCoordinates)
				: null;
		setLat(centroid ? String(centroid.y ?? "") : "");
		setLng(centroid ? String(centroid.x ?? "") : "");
		if (centroid) {
			onCoordsChange(centroid);
		}
	};

	const handlePlotNumberSelect = (plotNumber: number, trialId?: number | null): boolean => {
		const plot = plots.find((p) => p.plotId === plotNumber && p.fieldId === trialId);
		if (!plot) {
			toast.error(`No plot found for plot number ${plotNumber} in field ${trialId}.`);
			return false;
		}
		if (!plot.shapeCoordinates || plot.shapeCoordinates.length === 0) {
			toast.error(`Plot ${plotNumber} has no geolocation — cannot pan map to it.`);
		}
		setScope("Plot");
		handleElChange(String(plot.id), "Plot", true);
		return true;
	};

	const handleBlockIdSelect = (blockId: number, trialId?: number | null): boolean => {
		const block = blocks.find((b) => b.blockId === blockId && b.fieldId === trialId);
		if (!block) {
			toast.error(`No block found for block ${blockId}.`);
			return false;
		}
		setScope("Block");
		handleElChange(String(block.id), "Block", true);
		return true;
	};

	const describeBlockId = (blockId?: number | null, trialId?: number | null): string | null => {
		if (blockId == null) return null;
		const block = blocks.find((b) => b.blockId === blockId && b.fieldId === trialId);
		if (!block) return `Block ${blockId} (missing)`;
		const field = fields.find((f) => f.fieldId === block.fieldId);
		const parts = [
			`Block ${blockId}`,
			block.friendlyName ?? block.entityIdentifier,
			field?.friendlyName ?? field?.entityIdentifier,
		].filter(Boolean);
		return parts.join(" · ");
	};

	const describePlotNumber = (plotNumber?: number | null, trialId?: number | null): string | null => {
		if (plotNumber == null) return null;
		const plot = plots.find((p) => p.plotId === plotNumber && p.fieldId === trialId);
		if (!plot) return `Plot ${plotNumber} (missing)`;
		const block = blocks.find((b) => b.id === plot.blockId);
		const field = fields.find((f) => f.fieldId === plot.fieldId);
		const parts = [
			`Plot ${plotNumber}`,
			block?.friendlyName ?? block?.entityIdentifier,
			field?.friendlyName ?? field?.entityIdentifier,
		].filter(Boolean);
		return parts.join(" · ");
	};

	const handleSavePlacement = (e: React.FormEvent) => {
		e.preventDefault();
		const latNum = parseFloat(lat);
		const lngNum = parseFloat(lng);
		if (Number.isNaN(latNum) || Number.isNaN(lngNum)) return;

		const requiresEl = scope !== "Site";
		const experimentalLocationId = requiresEl && elId ? Number(elId) : NaN;
		const plotExperimentalLocationId = Number.isFinite(experimentalLocationId)
			? experimentalLocationId
			: null;

		if (requiresEl && plotExperimentalLocationId === null) {
			toast.error(`Select a ${scope.toLowerCase()} before saving placement.`);
			return;
		}

		if (!effectiveFrom) {
			toast.error("Effective from date is invalid.");
			return;
		}

		const effectiveToIso = effectiveTo ? dateStringToUtcIso(effectiveTo) : null;
		const effectiveFromIso = dateStringToUtcIso(effectiveFrom);

		if (
			effectiveToIso !== null &&
			effectiveToIso <= effectiveFromIso
		) {
			toast.error("Effective to must be after Effective from.");
			return;
		}

		onSavePlacement({
			geolocation: { x: lngNum, y: latNum },
			scope,
			plotExperimentalLocationId,
			effectiveFromUtc: effectiveFromIso,
			effectiveToUtc: effectiveToIso,
		});
	};

	const handleSaveHeights = (e: React.FormEvent) => {
		e.preventDefault();
		onSaveHeights({
			towerHeightInMeters: towerHeight ? parseFloat(towerHeight) : null,
		});
	};

	return (
		<div className="bg-white rounded-lg border border-gray-200 p-4 space-y-5">
			<div className="flex justify-between items-start">
				<div>
					<h3 className="text-base font-semibold text-gray-900">
						{channelLabel}
					</h3>
					{primarySensor.deviceFriendlyName && (
						<p className="text-sm text-gray-700 font-medium">
							{primarySensor.deviceFriendlyName}
						</p>
					)}
					{primarySensor.manufacturer && (
						<p className="text-sm text-gray-500">
							{primarySensor.manufacturer}
						</p>
					)}
					<p className="text-sm text-gray-500">
						Serial: {primarySensor.deviceSerialKey}
					</p>
					<p className="text-sm text-gray-400 mt-0.5">
						{sensors.map((s) => humanizeSensorType(s.measureName)).join(" · ")}
					</p>
				</div>
				<button
					type="button"
					onClick={onCancel}
					className="text-gray-400 hover:text-gray-600"
					aria-label="Close"
				>
					<svg
						aria-hidden="true"
						className="w-4 h-4"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M6 18L18 6M6 6l12 12"
						/>
					</svg>
				</button>
			</div>

			{sensors.length > 1 && (
				<p className="text-xs text-blue-600 bg-blue-50 border border-blue-200 rounded px-3 py-1.5">
					Placement applies to all {sensors.length} sensors on this channel.
				</p>
			)}

			{/* Heights — cup anemometer only; sonic tower height managed via TowerGroupConfigModal */}
			{hasWindSensor && !hasSonicSensor && (
				<details open>
					<summary className="text-sm font-semibold text-gray-600 cursor-pointer select-none py-1">
						Heights
					</summary>
					<form onSubmit={handleSaveHeights} className="mt-3 space-y-3">
						<div>
							<label
								htmlFor="tower-height"
								className="block text-sm font-medium text-gray-700 mb-1"
							>
								Tower height (m)
							</label>
							<input
								id="tower-height"
								type="number"
								step="0.01"
								value={towerHeight}
								onChange={(e) => setTowerHeight(e.target.value)}
								disabled={!canEdit}
								className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-smartfield bg-white text-gray-900 disabled:bg-gray-50 disabled:text-gray-400"
							/>
						</div>
						{canEdit && (
							<button
								type="submit"
								disabled={isSavingHeights}
								className="w-full px-3 py-1.5 text-sm bg-smartfield text-white rounded hover:bg-smartfield/90 disabled:opacity-50"
							>
								{isSavingHeights ? "Saving…" : "Save heights"}
							</button>
						)}
					</form>
					<HeightHistoryChart
						entries={placementHistoryEntries}
						field="tower"
					/>
				</details>
			)}

			{primarySensor.id != null && (
				<div>
					<h4 className="text-sm font-semibold text-gray-600 py-1">
						Placement history
					</h4>
					<PlacementHistoryTimeline
						entries={placementHistoryEntries}
						els={allTimeEls}
						field="tower"
						isLoading={placementHistoryQuery.isLoading}
						isError={placementHistoryQuery.isError}
					/>
				</div>
			)}

			<div>
				<h4 className="text-sm font-semibold text-gray-600 py-1">
					Placement
				</h4>
				<form onSubmit={handleSavePlacement} className="mt-3 space-y-3">
					<div className="grid grid-cols-2 gap-2">
						<div>
							<label
								htmlFor="placement-lat"
								className="block text-sm font-medium text-gray-700 mb-1"
							>
								Latitude
							</label>
							<input
								id="placement-lat"
								type="number"
								step="any"
								value={lat}
								onChange={(e) => {
									setLat(e.target.value);
									const parsedLat = parseFloat(e.target.value);
									const parsedLng = parseFloat(lng);
									if (
										Number.isFinite(parsedLat) &&
										Number.isFinite(parsedLng)
									) {
										onCoordsChange({ x: parsedLng, y: parsedLat });
									}
								}}
								disabled={!canEdit}
								className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-smartfield bg-white text-gray-900 disabled:bg-gray-50"
							/>
						</div>
						<div>
							<label
								htmlFor="placement-lng"
								className="block text-sm font-medium text-gray-700 mb-1"
							>
								Longitude
							</label>
							<input
								id="placement-lng"
								type="number"
								step="any"
								value={lng}
								onChange={(e) => {
									setLng(e.target.value);
									const parsedLng = parseFloat(e.target.value);
									const parsedLat = parseFloat(lat);
									if (
										Number.isFinite(parsedLng) &&
										Number.isFinite(parsedLat)
									) {
										onCoordsChange({ x: parsedLng, y: parsedLat });
									}
								}}
								disabled={!canEdit}
								className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-smartfield bg-white text-gray-900 disabled:bg-gray-50"
							/>
						</div>
					</div>

					{hasAerisSensor && chambers.length > 0 && (
						<div>
							<label
								htmlFor="chamber-quickpick"
								className="block text-sm font-medium text-gray-700 mb-1"
							>
								Chamber
							</label>
							<select
								id="chamber-quickpick"
								value={selectedChamberId}
								onChange={(e) => {
									const chamber = chambers.find(
										(c) => String(c.id) === e.target.value,
									);
									if (chamber?.plotNumber != null) {
										if (handlePlotNumberSelect(chamber.plotNumber, chamber.trialId)) {
											setSelectedChamberId(e.target.value);
										}
									} else if (chamber) {
										toast.error("Chamber is not mapped to a plot.");
									}
								}}
								disabled={!canEdit}
								className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-smartfield bg-white text-gray-900 disabled:bg-gray-50"
							>
								<option value="">Match to chamber…</option>
								{chambers.map((c) => {
									const plotInfo = describePlotNumber(c.plotNumber, c.trialId);
									return (
										<option key={c.id} value={c.id}>
											Chamber {c.chamberNumber} ({c.year})
											{plotInfo ? ` — ${plotInfo}` : " — unmapped"}
										</option>
									);
								})}
							</select>
							<p className="text-xs text-gray-400 mt-1">
								Chamber number matches this sensor's channel number by
								default. Selecting a chamber sets Scope to Plot and picks its
								mapped plot below.
							</p>
						</div>
					)}

					{(hasSonicSensor || hasMiroSensor) && towerOptions.length > 0 && (
						<div>
							<label
								htmlFor="tower-quickpick"
								className="block text-sm font-medium text-gray-700 mb-1"
							>
								Tower
							</label>
							<select
								id="tower-quickpick"
								value={selectedTowerId}
								onChange={(e) => {
									const tower = towers.find(
										(t) => String(t.id) === e.target.value,
									);
									if (tower?.blockId != null) {
										if (handleBlockIdSelect(tower.blockId, tower.trialId)) {
											setSelectedTowerId(e.target.value);
										}
									} else if (tower) {
										toast.error("Tower is not mapped to a block.");
									}
								}}
								disabled={!canEdit}
								className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-smartfield bg-white text-gray-900 disabled:bg-gray-50"
							>
								<option value="">Match to tower…</option>
								{towerOptions.map((t) => {
									const blockInfo = describeBlockId(t.blockId, t.trialId);
									return (
										<option key={t.id} value={String(t.id)}>
											{t.towerName} ({t.year})
											{blockInfo ? ` — ${blockInfo}` : " — unmapped"}
										</option>
									);
								})}
							</select>
							<p className="text-xs text-gray-400 mt-1">
								Selecting a tower sets Scope to Block and picks its mapped block
								below.
								{miroChannel != null &&
									` Only towers that own Miro channel ${miroChannel} are listed.`}
							</p>
						</div>
					)}

					<fieldset>
						<legend className="block text-sm font-medium text-gray-700 mb-1">
							Scope
						</legend>
						<div className="flex gap-3">
							{(["Plot", "MiniBlock", "Block", "Field", "Site"] as const).map((s) => (
								<label
									key={s}
									className="flex items-center gap-1 text-sm text-gray-700 cursor-pointer"
								>
									<input
										type="radio"
										name="scope"
										value={s}
										checked={scope === s}
										onChange={() => handleScopeChange(s)}
										disabled={!canEdit}
										className="text-smartfield"
									/>
									{s}
								</label>
							))}
						</div>
					</fieldset>

					{scope === "Plot" && (
						<div>
							<label
								htmlFor="scope-plot"
								className="block text-sm font-medium text-gray-700 mb-1"
							>
								Plot
							</label>
							<select
								id="scope-plot"
								value={elId}
								onChange={(e) => handleElChange(e.target.value)}
								disabled={!canEdit}
								className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-smartfield bg-white text-gray-900 disabled:bg-gray-50"
							>
								<option value="">Select plot…</option>
								{sortedPlots.map((p) => (
									<option key={p.id} value={String(p.id)}>
										{p.friendlyName ?? p.entityIdentifier ?? `Plot ${p.plotId}`}
									</option>
								))}
							</select>
						</div>
					)}

					{scope === "Field" && (
						<div>
							<label
								htmlFor="scope-field"
								className="block text-sm font-medium text-gray-700 mb-1"
							>
								Field
							</label>
							<select
								id="scope-field"
								value={elId}
								onChange={(e) => handleElChange(e.target.value)}
								disabled={!canEdit}
								className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-smartfield bg-white text-gray-900 disabled:bg-gray-50"
							>
								<option value="">Select field…</option>
								{sortedFields.map((f) => (
									<option key={f.id} value={String(f.id)}>
										{f.friendlyName ??
											f.entityIdentifier ??
											`Field ${f.fieldId}`}
									</option>
								))}
							</select>
						</div>
					)}

					{scope === "Block" && (
						<div>
							<label
								htmlFor="scope-block"
								className="block text-sm font-medium text-gray-700 mb-1"
							>
								Block
							</label>
							<select
								id="scope-block"
								value={elId}
								onChange={(e) => handleElChange(e.target.value)}
								disabled={!canEdit}
								className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-smartfield bg-white text-gray-900 disabled:bg-gray-50"
							>
								<option value="">Select block…</option>
								{sortedBlocks.map((b) => (
									<option key={b.id} value={String(b.id)}>
										{b.friendlyName ?? b.entityIdentifier ?? `Block ${b.id}`}
									</option>
								))}
							</select>
						</div>
					)}

					{scope === "MiniBlock" && (
						<div>
							<label
								htmlFor="scope-miniblock"
								className="block text-sm font-medium text-gray-700 mb-1"
							>
								Mini block
							</label>
							<select
								id="scope-miniblock"
								value={elId}
								onChange={(e) => handleElChange(e.target.value)}
								disabled={!canEdit}
								className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-smartfield bg-white text-gray-900 disabled:bg-gray-50"
							>
								<option value="">Select mini block…</option>
								{sortedMiniBlocks.map((mb) => (
									<option key={mb.id} value={String(mb.id)}>
										{mb.friendlyName ??
											mb.entityIdentifier ??
											`Mini block ${mb.id}`}
									</option>
								))}
							</select>
						</div>
					)}

					<div>
						<label
							htmlFor="effective-from"
							className="block text-sm font-medium text-gray-700 mb-1"
						>
							Effective from
						</label>
						<input
							id="effective-from"
							type="date"
							value={effectiveFrom}
							onChange={(e) => setEffectiveFrom(e.target.value)}
							disabled={!canEdit}
							className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-smartfield bg-white text-gray-900 disabled:bg-gray-50"
						/>
					</div>

					<div>
						<label
							htmlFor="effective-to"
							className="block text-sm font-medium text-gray-700 mb-1"
						>
							Effective to (leave empty = open)
						</label>
						<input
							id="effective-to"
							type="date"
							value={effectiveTo}
							onChange={(e) => setEffectiveTo(e.target.value)}
							disabled={!canEdit}
							className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-smartfield bg-white text-gray-900 disabled:bg-gray-50"
						/>
					</div>

					{canEdit && (
						<button
							type="submit"
							disabled={isSavingPlacement || !lat || !lng}
							className="w-full px-3 py-1.5 text-sm bg-smartfield text-white rounded hover:bg-smartfield/90 disabled:opacity-50"
						>
							{isSavingPlacement ? "Saving…" : "Save placement"}
						</button>
					)}
				</form>
			</div>
		</div>
	);
}
